import DOMPurify from "dompurify";
import type { DomPurifyConfigForReturnType } from "./adapters/ISanitizerConfigAdapter";
import type { OwaSanitizerConfig } from "./owaSanitizerConfig";
import { getSanitizerConfigAdapter } from "./adapters/SanitizerConfigAdapter";
import { getAllowedUriRegExp } from "./adapters/uriPostProcessor";
import { SanitizerReturnType } from "./sanitizerReturnType";

/**
 * Builds a DOMPurify config from the OWA extended config, including URI filtering.
 *
 * Refresh the shared config's URI regexp in place before each sanitize call.
 * The regexp is already cached; resolving it here also honors test resets.
 * Unknown-protocol configs have separate adapter memo entries.
 */
function buildDomPurifyConfig<T extends SanitizerReturnType>(config: OwaSanitizerConfig, returnType: T): DomPurifyConfigForReturnType<T> {
    const domPurifyConfig = getSanitizerConfigAdapter().toDomPurifyConfig(config, returnType);
    if (config.allowUnknownProtocols) {
        return domPurifyConfig;
    }
    domPurifyConfig.ALLOWED_URI_REGEXP = getAllowedUriRegExp();
    return domPurifyConfig;
}

/**
 * Dispatches to the appropriate DOMPurify function based on return type so
 * `getTrustedHTML` can delegate to either path depending on `shouldUseNativeSanitizer()`.
 */
export function sanitizeWithDomPurify<T extends SanitizerReturnType>(html: string, returnType: T, config: OwaSanitizerConfig): string | HTMLElement | DocumentFragment | Node {
    switch (returnType) {
        case SanitizerReturnType.TrustedHTML:
            if (window.trustedTypes) {
                return DOMPurify.sanitize(html, buildDomPurifyConfig(config, SanitizerReturnType.TrustedHTML)) as unknown as string;
            }
            return DOMPurify.sanitize(html, buildDomPurifyConfig(config, SanitizerReturnType.String));
        case SanitizerReturnType.HTMLElement:
        case SanitizerReturnType.Node:
            return DOMPurify.sanitize(html, buildDomPurifyConfig(config, SanitizerReturnType.HTMLElement));
        case SanitizerReturnType.DocumentFragment:
            return DOMPurify.sanitize(html, buildDomPurifyConfig(config, SanitizerReturnType.DocumentFragment));
        case SanitizerReturnType.String:
        default:
            return DOMPurify.sanitize(html, buildDomPurifyConfig(config, SanitizerReturnType.String));
    }
}
